import Accordion, { AccordionItem } from '@repo/ui/src/components/Accordion';
import Image from '@repo/ui/src/components/Image/New';
import { Anchor } from '@repo/ui/src/components/Anchor';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import Product from '@models/product';
import { VariationGroup } from '@typings/product';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';

export interface _TierTwoVariationsProps {
  product: Product;
}

const TierTwoVariations = ({ product }: _TierTwoVariationsProps) => {
  const { push } = useCurrentUrl();
  const { t } = useTranslation();
  const [disabled, setDisabled] = React.useState(false);

  const categoryVariationGroups = React.useMemo(() => {
    const categoryVariationsTypes = product.variationAttributes?.find(
      (item) => item.id === VariationGroup.category,
    )?.values;

    return (
      categoryVariationsTypes
        ?.map((category) => {
          return {
            value: category.value,
            variations: product.variations.filter((variation) =>
              variation.variationValues.every(
                ({ name, value }) => name === VariationGroup.category && value === category.value,
              ),
            ),
          };
        })
        .filter(({ variations }) => variations.length) ?? []
    );
  }, [product]);

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    setDisabled(true);
    push(e.target.value);
  };

  if (!categoryVariationGroups.length) {
    return null;
  }

  return (
    <>
      <div className="md:tw-hidden tw-grid tw-gap-y-2">
        <h4 className="tra-text-web-utl-link">
          {t('productDetailPageContent.tierTwoSelectLabel', undefined, {
            default: 'Select an option',
          })}
        </h4>
        <div className="tw-relative tw-grid after:tw-block after:tw-absolute after:tw-bg-dropdown after:tw-bg-cover after:tw-inset-y-4 after:tw-right-4 after:tw-w-6 after:tw-pointer-events-none">
          <select
            onChange={handleSelect}
            className="tw-bg-white tw-border-2 tw-border-surface-450 tw-px-4 tw-py-3 tw-w-full disabled:tw-text-surface-450"
            disabled={disabled}
          >
            {categoryVariationGroups.map(({ value, variations }) => (
              <optgroup label={value} key={value}>
                {variations.map((variation) => (
                  <option
                    key={variation.sku}
                    value={variation.productUrl}
                    selected={variation.id === product.sku}
                  >
                    {variation.title}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>
      <Accordion
        preExpanded={categoryVariationGroups.map((_, i) => i)}
        allowMultipleExpanded
        allowZeroExpanded
        className="tw-hidden md:tw-block"
      >
        {categoryVariationGroups.map(({ value, variations }, index) => (
          <AccordionItem
            key={index}
            uuid={index}
            heading={value}
            headerFontClass="tra-text-web-utl-link"
            headerClassName="!tw-p-0"
          >
            <ul className="tw-list-none tw-flex tw-flex-wrap tw-gap-y-4 tw-gap-x-6 tw-px-0">
              {variations.map((variation) => (
                <li key={variation.title}>
                  <Anchor
                    link={variation.productUrl}
                    className={classNames(
                      'tw-grid tw-py-[18.5px] tw-px-3 tw-rounded tw-relative tw-group/item tw-bg-white',
                      'after:tw-absolute after:-tw-inset-0 after:tw-transition after:tw-duration-300 after:tw-ease-in-out after:tw-bg-gradient-to-br after:tw-from-transparent after:tw-via-transparent after:tw-to-warm-300 after:tw-opacity-0',
                      // hover
                      'hover:after:tw-opacity-100',
                      variation.id === product.sku &&
                        (variation.isOrderable ? 'tw-border-brand-300' : 'tw-border-neutral-500'),
                      variation.id !== product.sku
                        ? 'tw-border-2 tw-border-surface-450'
                        : 'tw-border-4',
                    )}
                  >
                    <Image
                      src={buildImageSrc(undefined, 80, 1, 1, variation.mainImageUrl)}
                      alt={variation.title}
                      width={80}
                      height={80}
                      className={classNames('tw-z-20', !variation.isOrderable && 'tw-grayscale')}
                    />
                    {!variation.isOrderable && (
                      <span className="tw-transition-opacity tw-absolute tw-z-20 tw-self-end tw-text-center tw-w-full tw-text-white tw-bg-surface-450 tw-py-1 tra-text-web-utl-label-s">
                        Out of stock
                      </span>
                    )}
                    <span className="tw-grid tw-justify-items-center tw-absolute tw-top-full tw-transition-opacity tw-opacity-0 group-hover/item:tw-opacity-100 tra-text-web-body-l tw-text-white tw-bg-transparent-light-500 tw-text-nowrap tw-p-2.5 tw-justify-self-center tw-z-30 tw-rounded tw-pointer-events-none before:tw-absolute before:tw-bottom-full before:tw-border-8 before:tw-border-b-transparent-light-500 before:tw-border-transparent before:tw-justify-self-center">
                      {variation.title}
                    </span>
                  </Anchor>
                </li>
              ))}
            </ul>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  );
};

export default TierTwoVariations;
